export type InstructionLayer = 'beginner' | 'practitioner' | 'expert';

export interface InstructionContent {
  layer: InstructionLayer;
  title: string;
  subtitle: string;
  whatIs: {
    title: string;
    content: string;
  };
  howToWrite: {
    title: string;
    sections: {
      title: string;
      content: string[];
    }[];
  };
  tips?: string[];
  pitfalls?: string[];
}

export const instructions: Record<InstructionLayer, InstructionContent> = {
  beginner: {
    layer: 'beginner',
    title: 'Beginner',
    subtitle: 'Just help me do it right',
    whatIs: {
      title: 'What an Outcome Driver is',
      content: 'An Outcome Driver explains why something needs to change. It has three parts: Current State — What\'s objectively true today; Problem / Opportunity — What those facts mean; Ramifications — Why it matters enough to fix.'
    },
    howToWrite: {
      title: 'How to Write One (Beginner Version)',
      sections: [
        {
          title: 'Current State:',
          content: ['Write the facts. Stick to what\'s observable.', 'If a camera can see it, it belongs here.']
        },
        {
          title: 'Problem / Opportunity:',
          content: ['Explain the issue created by those facts.', 'Why is this a problem? What\'s the tension?']
        },
        {
          title: 'Ramifications:',
          content: ['Describe what happens if nothing changes.', 'Why does this matter now? What\'s at stake?']
        }
      ]
    },
    tips: [
      'One short paragraph per Outcome Driver.',
      'Avoid solutions or plans.',
      'Keep it honest and concrete, not abstract.',
      'Stop when someone else could understand why this matters.'
    ]
  },
  practitioner: {
    layer: 'practitioner',
    title: 'Practitioner',
    subtitle: 'I want to wield the tool correctly, not sloppily',
    whatIs: {
      title: 'What an Outcome Driver is doing',
      content: 'A good Outcome Driver: Creates shared understanding; Establishes the compelling reason to change; Frames the boundaries of decision-making; Anchors Desired Outcomes.'
    },
    howToWrite: {
      title: 'How to Write One (Practitioner Version)',
      sections: [
        {
          title: '1. Current State (Facts Only)',
          content: [
            'Ask: What is observably true right now? What conditions are producing friction? What patterns are repeating?',
            'Guidelines: No theories. No "why." No future thinking. No stakes. No meaning-making.'
          ]
        },
        {
          title: '2. Problem / Opportunity (Essence of the Issue)',
          content: [
            'Ask: What do these facts add up to? What\'s the core tension or mismatch? What truth needs to be acknowledged?',
            'Guidelines: One or two sentences. Distill, don\'t describe. This is the interpretation, not the facts.'
          ]
        },
        {
          title: '3. Ramifications (Consequences of Inaction)',
          content: [
            'Ask: What happens if nothing changes? Why is this untenable? What cost will keep rising?',
            'Guidelines: Be direct and honest. Show the stakes without exaggeration. This section provides urgency.'
          ]
        }
      ]
    },
    pitfalls: [
      'Mixing facts and meaning',
      'Jumping to solutions',
      'Writing too vaguely',
      'Writing too abstractly',
      'Over-explaining instead of distilling',
      'Describing symptoms, not the underlying issue'
    ]
  },
  expert: {
    layer: 'expert',
    title: 'Expert',
    subtitle: 'For those who need mastery and discernment',
    whatIs: {
      title: 'Expert Understanding',
      content: 'An Outcome Driver is a boundary-setting document that shapes the entire prioritization effort. It must: Capture what is structurally or systemically true; Surface the non-obvious tension beneath the surface; Reveal what\'s unsustainable; Articulate the cost of inaction; Create alignment by confronting the reality others avoid; Hold intellectual honesty; Operate at the right altitude (not too granular, not too general).'
    },
    howToWrite: {
      title: 'How to Write One (Expert Version)',
      sections: [
        {
          title: '1. Current State',
          content: [
            'Write only what is structurally, observably, irrefutably true. Strip away all interpretation.',
            'An expert asks: What are the governing facts? What are the load-bearing conditions? What would a disinterested observer say?',
            'An expert removes: Contextual noise; Stories about why things are the way they are; Opinions, even when they feel true.'
          ]
        },
        {
          title: '2. Problem / Opportunity',
          content: [
            'Compress the essence into a single non-negotiable insight that reframes the situation.',
            'An expert asks: What truth is this system trying to avoid? What is the underlying tension that creates drag, waste, or risk? What\'s the mismatch between the system we have and the one we need?',
            'The expert version of the Problem statement is: short; sharp; clean; uncomfortable; undeniable.'
          ]
        },
        {
          title: '3. Ramifications',
          content: [
            'Describe the structural consequences of continued avoidance or inaction.',
            'An expert asks: What trajectory are we on? What will predictably fail if we do nothing? What value or opportunity will collapse because of delay?',
            'The expert version: Names the real cost; Avoids emotion-driven framing; Creates urgency without theatrics; Forces clarity.'
          ]
        }
      ]
    },
    pitfalls: [
      'Being too "smart" and losing clarity',
      'Writing analysis instead of drivers',
      'Using business vocabulary instead of plain language',
      'Hiding stakes behind polite phrasing',
      'Creating drivers at the wrong altitude (too small or too sweeping)'
    ]
  }
};

export const jediInstructions = {
  buckets: {
    'current-state': 'Name only what is.',
    'problem-opportunity': 'Name the tension.',
    'ramifications': 'Name the cost of staying here.'
  },
  rules: [
    'Rule 1: If a camera can see it → Current State',
    'Rule 2: If the sentence answers "So what?" → Problem',
    'Rule 3: If the sentence answers "Why now?" → Ramifications'
  ],
  nudges: {
    'current-state': 'Strip it to what\'s true.',
    'problem-opportunity': 'Say the part that hurts.',
    'ramifications': 'Say the part you\'re afraid will happen.'
  },
  examples: {
    'current-state': 'Harry brings in all client revenue.',
    'problem-opportunity': 'The business depends on one person.',
    'ramifications': 'If he stops, the business stops.'
  }
};

export const postDraftChecklist = {
  structural: {
    title: 'A. STRUCTURAL CHECK',
    sections: [
      {
        title: '1. Current State',
        checks: [
          'Are all sentences factual and observable?',
          'Did I avoid interpretation?',
          'Did I avoid stakes or consequences?',
          'Could someone else verify the statements?',
          'Did I strip out theories and explanations?'
        ]
      },
      {
        title: '2. Problem / Opportunity',
        checks: [
          'Is the problem the meaning of the facts?',
          'Is the core tension expressed cleanly?',
          'Is it one or two sentences max?',
          'Does it avoid restating the Current State?',
          'Is it free from consequences or stakes?'
        ]
      },
      {
        title: '3. Ramifications',
        checks: [
          'Does it answer "What happens if nothing changes?"',
          'Are stakes clear and compelling?',
          'Does it avoid new facts?',
          'Does it avoid describing solutions or plans?',
          'Does the section create urgency?'
        ]
      }
    ]
  },
  quality: {
    title: 'B. QUALITY CHECK',
    checks: [
      'Is each Outcome Driver a single coherent narrative?',
      'Is the language concrete rather than vague?',
      'Does it avoid abstraction and consultant-speak?',
      'Is the altitude right (not too tactical, not too high-level)?',
      'Is the writing honest?',
      'Would someone who knows nothing about the situation understand why it matters?'
    ]
  },
  complementary: {
    title: 'C. COMPLEMENTARY SET CHECK (for multi-driver contexts)',
    checks: [
      'Do the drivers describe distinct, non-overlapping reasons for change?',
      'Do they form a complementary set?',
      'Are they ordered from most compelling to least?',
      'Is each driver operating at the same altitude?',
      'Is anything redundant?',
      'Is anything missing?'
    ]
  },
  jedi: {
    title: 'D. "JEDI" CHECK (the final test)',
    checks: [
      'Fact → Meaning → Consequence structure is unmistakably clear.',
      'Each section is doing its unique job.',
      'Nothing is mixed.',
      'The Outcome Driver holds together as a compelling reason for change.',
      'I would feel confident presenting this as the motivation behind a major decision.'
    ]
  }
};



